import { useState } from "react";
import * as userService from "../services/userService";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (u: userService.UsuarioAdmin) => void;
};

function CreateUserModal({ open, onClose, onCreated }: Props) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState("EVENT_ADMIN");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!open) return null;

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setLoading(true);

    try {
      const created = await userService.crearUsuario({
        nombre,
        email,
        telefono: telefono.trim() ? "+51" + telefono : null,
        password,
        rol,
      });
      onCreated(created);
      // Limpieza
      setNombre("");
      setEmail("");
      setTelefono("");
      setPassword("");
      setRol("EVENT_ADMIN");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error al crear usuario");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Cerrar"
      />

      {/* Card */}
      <div className="relative w-full max-w-[560px] rounded-2xl border border-white/10 bg-surface-darker shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-serif text-white">Crear usuario</h3>
            <p className="text-sm text-slate-400">
              Completa los datos para registrar un nuevo usuario.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Cerrar modal"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={crear} className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Nombre</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Nombres y Apellidos"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Teléfono</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="Opcional"
              />
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-sm font-medium text-white">Correo</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                type="email"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Contraseña</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                type="password"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Rol</label>
              <select
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
              >
                <option value="EVENT_ADMIN">Administrador de evento</option>
                <option value="SYSTEM_ADMIN">Administrador del sistema</option>
              </select>
            </div>
          </div>

          {errorMsg && <p className="text-sm text-red-300">{errorMsg}</p>}

          {/* Footer botones */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 h-11 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 h-11 rounded-lg bg-gold-accent hover:bg-[#c5a028] text-surface-darker font-bold transition-all shadow-lg shadow-gold-accent/20 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateUserModal;